
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Mutation } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import { AlarmAction, AlarmActionType, Response } from "@/store/responses/types";
import { VueBootstrapField } from "@/store/types";
import ActionSetupEntityModal from "@/components/setup/alarm-setup/actions/ActionSetupEntityModal.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

const Responses = namespace("responses");

interface stringFormattedDictionaryObject {
	emphasised: boolean;
	value: string;
}

@Component({
	components: {
		"action-setup-entity-modal": ActionSetupEntityModal,
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class ActionSetupModal extends Vue {
	@Responses.Action private getAlarmActions: (alarmId: number) => Promise<void>;
	@Responses.Mutation private setAlarmActions: (alarmActions: AlarmAction[]) => void;
	@Responses.Mutation private setAlarmActionsVisible: (isVisible: boolean) => void;
	@Responses.Mutation private setCurrentAlarmAction: (alarmAction: AlarmAction) => void;
	@Responses.State private alarmActions: AlarmAction[];
	@Responses.State private currentAlarm: Response;
	@Responses.State private currentAlarmAction: AlarmAction;
	@Responses.State private supportedAlarmActionTypes: AlarmActionType[];

	private searchTerm: string = "";
	private tableIsBusy: boolean = false;

	private tableFields: VueBootstrapField[] = [
		{
			key: "responseActionTypeID",
			label: "Type",
			sortable: true,
			formatter: alarmActionTypeId => {
				return this.getActionTitle(alarmActionTypeId);
			},
			filterByFormatted: true
		},
		{ key: "description", label: "Description", sortable: true },
		{ key: "actions", label: "Actions", sortable: false }
	];

	private async created(): Promise<void> {
		this.tableIsBusy = true;
		await this.getAlarmActions(this.currentAlarm.responseID);
		this.tableIsBusy = false;
	}

	private get filterFields(): string[] {
		return ["responseActionTypeID", "description"];
	}

	private getActionTitle(alarmActionTypeId: number): string {
		return this.supportedAlarmActionTypes.find(x => x.responseActionTypeId === alarmActionTypeId).title;
	}

	private handleNewActionClick(): void {
		this.setCurrentAlarmAction({
			responseID: this.currentAlarm.responseID
		} as AlarmAction);
	}
	private handleEditClick(action: AlarmAction): void {
		this.setCurrentAlarmAction(action);
	}

	private handleModalHidden(): void {
		this.setAlarmActionsVisible(false);
	}

	private destroyed(): void {
		this.setAlarmActions([]);
	}

	/**
	 * @summary This function uses a regular expression to match strings <***>like this</***>.
	 * @param rawString The string to match the regular expression on.
	 * @returns A dictionary, containing a variable amount of entries.
	 * 			Each property contains an object stating the string value and if it should be emphasised or not.
	 */
	private getDescriptionParts(rawString: string): Record<string, stringFormattedDictionaryObject> {
		const boldPartRegEx = /<\*\*\*>([^<]{1,})<\/\*\*\*>/g;

		let match;
		let startIndex = 0;
		let currentStringNumber = 1;
		let stringFormattedDictionary = {} as Record<string, stringFormattedDictionaryObject>;

		while ((match = boldPartRegEx.exec(rawString)) !== null) {
			const descriptionBeginningPart = rawString.substring(startIndex, rawString.indexOf(match[0])).trim();

			//our non-matches should not be emphasised
			if (descriptionBeginningPart) {
				stringFormattedDictionary[`string${currentStringNumber++}`] = {
					emphasised: false,
					value: descriptionBeginningPart
				};
			}

			//our regex match should be emphasised
			stringFormattedDictionary[`string${currentStringNumber++}`] = {
				emphasised: true,
				value: match[1].trim()
			};

			//set startIndex for next loop
			startIndex += boldPartRegEx.lastIndex - startIndex;
		}

		//add whatever is leftover as non-emphasised
		if (startIndex < rawString.length) {
			stringFormattedDictionary[`string${currentStringNumber}`] = {
				emphasised: false,
				value: rawString.substring(startIndex).trim()
			};
		}

		return stringFormattedDictionary;
	}
}
